<template>
  <div>
    <v-sheet class="transparent-background-color">
      <v-card class="page-card">
        <v-card-title class="mx-3">
          {{
            $t(
              isNew
                ? "CRUD.newInfo"
                : savable
                ? "CRUD.editInfo"
                : "CRUD.viewInfo",
              [$t("StockArea.ObjectName")]
            )
          }}
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                @click="
                  $emit('mode', null);
                  $emit('input', null);
                "
                >fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("StockArea.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-card-text>
          <ValidationObserver ref="observer">
            <v-container>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("StockArea.Fields.stock_area_name") + $t("Global.colon")
                  }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('StockArea.Fields.stock_area_name')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="stockArea.area_name"
                      :hint="isNew ? '' : $t('StockArea.codeHint')"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'code')
                          .concat(errors)
                      "
                    >
                      <template v-slot:message="{ key, message }">
                        <div :class="key == 0 ? 'danger--text' : ''">
                          {{ message }}
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stockArea.area_name }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("StockArea.Fields.stock_area_code") + $t("Global.colon")
                  }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('StockArea.Fields.stock_area_code')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="stockArea.area_code"
                      :hint="isNew ? '' : $t('Stock.nameHint')"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'name')
                          .concat(errors)
                      "
                    >
                      <template v-slot:message="{ key, message }">
                        <div :class="key == 0 ? 'danger--text' : ''">
                          {{ message }}
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stockArea.area_code }}
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("StockArea.Fields.memo") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-textarea outlined v-model="stockArea.memo"></v-textarea>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stockArea.memo }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("StockArea.Fields.type") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-radio-group row v-model="stockArea.type">
                    <v-radio
                      v-for="type in $consts.stockArea.type"
                      :key="type.value"
                      :label="`${type.text}`"
                      :value="type.value"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <v-chip
                    :color="stockArea.type | stockAreaTypeColor"
                    :text-color="stockArea.type | stockAreaTypeTextColor"
                    small
                    label
                  >
                    {{ stockArea.type | stockAreaTypeText }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("StockArea.Fields.pick_sort") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-text-field
                    single-line
                    v-model="stockArea.pick_sort"
                    type="number"
                    @input="
                      stockArea.pick_sort = $numberFormatter.onlyInt(
                        stockArea.pick_sort
                      )
                    "
                    @blur="
                      stockArea.pick_sort = $numberFormatter.onlyInt(
                        stockArea.pick_sort
                      )
                    "
                  />
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stockArea.pick_sort }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("StockArea.Fields.max_rank") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-text-field
                    single-line
                    v-model="stockArea.max_rank"
                    type="number"
                    min="0"
                    @input="
                      stockArea.max_rank = $numberFormatter.onlyInt(
                        stockArea.max_rank
                      )
                    "
                    @blur="
                      stockArea.max_rank = $numberFormatter.onlyInt(
                        stockArea.max_rank
                      )
                    "
                  />
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stockArea.max_rank }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("StockArea.Fields.status") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-radio-group row v-model="stockArea.status">
                    <v-radio
                      :label="$t('StockArea.status.on')"
                      :key="1"
                      :value="1"
                    />
                    <v-radio
                      :label="$t('StockArea.status.off')"
                      :key="0"
                      :value="0"
                    />
                  </v-radio-group>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <v-chip
                    :color="stockArea.status ? 'primary' : ''"
                    :text-color="stockArea.status ? 'white' : ''"
                    small
                    label
                  >
                    {{
                      stockArea.status
                        ? $t("StockArea.status.on")
                        : $t("StockArea.status.off")
                    }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-container>
          </ValidationObserver>
        </v-card-text>
      </v-card>
      <v-card class="page-opts">
        <v-card-title>
          <v-row dense>
            <v-col cols="12">
              <v-btn
                outlined
                color="secondary"
                class="elevation-1"
                block
                @click="
                  $emit('input', null);
                  $emit('mode', null);
                "
              >
                <v-icon small class="pe-1">fa-times</v-icon>
                {{ $t("CRUD.backToList", [$t("StockArea.ObjectName")]) }}
              </v-btn>
            </v-col>
            <v-col cols="12" v-if="isView">
              <v-btn
                color="accent"
                block
                :disabled="loading && !stockArea.id"
                @click="$emit('mode', 'edit')"
              >
                <v-icon small class="pe-1">fa-pencil-alt</v-icon>
                {{ $t("CRUD.editInfo", [$t("StockArea.ObjectName")]) }}
              </v-btn>
            </v-col>
            <v-col cols="12" v-if="savable">
              <v-btn
                color="success"
                block
                :disabled="loading && !stockArea.id"
                @click="save()"
              >
                <v-icon small class="pe-1">fa-save</v-icon>
                {{ $t("CRUD.saveInfo", [$t("StockArea.ObjectName")]) }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
      </v-card>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Consts from "@/consts.js";
import Required from "@/components/plugins/Required";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import StockAreas from "@/api/stock_areas";

export default {
  name: "StockAreaDetail",

  props: ["value", "mode"],

  components: {
    Required,
    ValidationObserver,
    ValidationProvider,
  },

  computed: {
    isView() {
      return !this.mode || this.mode == "view";
    },
    savable() {
      return this.mode == "edit" || this.mode == "new";
    },
    isNew() {
      return this.mode == "new";
    },
    status() {
      return Consts.stockArea.status;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  data: () => ({
    stockArea: {},
    serverErrors: [],
  }),

  methods: {
    save() {
      if (this.mode == "new") {
        this.stockArea.stock_id = this.$route.query.stock_id;
      }
      this.$store.dispatch("fullscreenLoading");
      this.serverErrors = []; //重置远端错误提示
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            //校验成功再提交
            StockAreas.save(this.stockArea.id, this.stockArea)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", this.stockArea);
                    this.$emit("mode", null);
                    this.$emit("input", null);
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                  }
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              )
              .catch(
                function () {
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          } else {
            this.$store.dispatch("fullscreenLoadingStop");
          }
        }.bind(this)
      );
    },
  },

  watch: {
    value: {
      immediate: true, //初始赋值也调用
      handler() {
        this.serverErrors = []; //重置远端错误提示
        if (this.value && this.value.id) {
          StockAreas.fetchOne(this.value.id).then(
            function (response) {
              if (response.data.status.isOK()) {
                this.stockArea = response.data.stock_area;
              }
            }.bind(this)
          );
        } else {
          // 新建库区，初始化默认值
          this.stockArea = {
            status: 1,
          };
        }
        this.$scrollTop(); //返回顶部
      },
    },
  },
};
</script>